import { useListPick } from '@components/ListPicker/useListPick';
import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import Autocomplete from '@components/v2/form/Autocomplete/Autocomplete';
import Switch from '@components/v2/form/Switch/Switch';
import Modal from '@components/v2/ui/Modal/Modal';
import { ModalFooterConfig } from '@components/v2/ui/Modal/modal.types';
import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { Local } from '../../../../../../../../services/modules/locals/locals.types';
import { ShippingCompany } from '../../../../../../../../services/modules/shippingCompanies/shippingCompanies.types';
import useGeneralSettingsStore from '../../../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { formatCNPJ } from '../../../../../../../../utils/formatters';
import { Collection } from '../../../../../../../collections/collections.types';
import { useApplicationContext } from '../../../../../../../context/ApplicationContext/ApplicationContext';
import { BrandFreightType } from '../../../../../../../freight-type/brand-freight-type.types';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import useOrderDefinitions from '../../../../../../conditions/hooks/useOrderDefinitions';
import { SaleType } from '../../../../../../conditions/interfaces/orderCondition.interface';
import { OrderCondition } from '../../../../../../conditions/interfaces/orderCondition.interface';
import { getOrderConditionFieldsWithError } from '../../../../../../conditions/orderConditionFieldErrors.utils';
import { useOrderEditSectionsContext } from '../../../../../../edit/sections/OrderEditSections.context';
import {
  Order,
  OrderBatchItemsType,
  OrderClassificationType,
  OrderOperationsIntegrationType,
  OrderTransportCondition,
  orderChargeTypesOptions,
} from '../../../../../../order.types';
import OrderConditionApplyToAllModal from '../OrderConditionApplyToAllModal';
import { hasFieldError, isShippingCompanyVisible } from '../orderConditionDetails.utils';
import OrderConditionCustomFieldControl from './OrderConditionCustomFieldControl';
import useOrderConditionDefinitionsForm from './hooks/useOrderConditionDefinitionsForm';
import { OrderConditionCodeOption } from './orderConditionDefinitionsModal.types';

interface OrderConditionDefinitionsModalProps {
  order: Order;
  condition: OrderCondition;
  isOpen: boolean;
  isReadOnlyView: boolean;
  onClose: () => void;
}

const OrderConditionDefinitionsModal = ({
  order,
  condition,
  isOpen,
  isReadOnlyView,
  onClose,
}: OrderConditionDefinitionsModalProps) => {
  const { translate } = useTranslation();
  const { isBrand, hasTotvsIntegration } = useApplicationContext();
  const { generalSettings } = useGeneralSettingsStore();
  const { orderDefinitions, isLoadingOrderDefinitions, hasLoadedOrderDefinitions } =
    useOrderDefinitions();
  const { rawConditionErrors } = useOrderEditSectionsContext();
  const fieldsWithError = getOrderConditionFieldsWithError(rawConditionErrors, condition.id);
  const {
    formMethods,
    isSaving,
    customFields,
    customFieldValues,
    setCustomFieldValue,
    setSelectedOrderOperation,
    setSelectedOrderBatchItem,
    save,
    applyToAll,
  } = useOrderConditionDefinitionsForm({
    order,
    condition,
    isOpen,
    orderDefinitions,
    hasLoadedOrderDefinitions,
    onClose,
  });

  const { control, setValue } = formMethods;
  const classificationTypeCode = useWatch({ control, name: 'orderClassificationTypeCode' });
  const [brandFreightTypeCode, setBrandFreightTypeCode] = useState(
    condition.brandFreightType?.code ?? null,
  );

  const [syncedConditionKey, setSyncedConditionKey] = useState<string | null>(null);
  const conditionKey = isOpen ? condition.id : null;

  if (conditionKey !== syncedConditionKey) {
    setSyncedConditionKey(conditionKey);

    if (conditionKey) {
      setBrandFreightTypeCode(condition.brandFreightType?.code ?? null);
    }
  }

  const showClassification = orderDefinitions.orderClassificationsIntegration && isBrand;

  const canFetchClassifications = showClassification && hasTotvsIntegration;

  const classificationTypesResource = useListPick<OrderClassificationType>({
    url: '/order-classifications',
    limit: 20,
    shouldFetch: canFetchClassifications,
  });

  useEffect(() => {
    if (isOpen && canFetchClassifications) {
      classificationTypesResource.onSearch('');
    }
  }, [isOpen, canFetchClassifications]);

  const selectedClassificationType = classificationTypesResource.leftItems.find(
    (type) => type.code === classificationTypeCode,
  );

  const hasFreightQuote =
    generalSettings.showOrderConditionShippingCost && orderDefinitions.hasActiveTmsIntegration;

  const isShippingCompanyFieldVisible =
    hasLoadedOrderDefinitions &&
    !hasFreightQuote &&
    isShippingCompanyVisible({
      brandFreightTypeCode,
      orderTransportCondition: orderDefinitions.orderTransportCondition,
    });

  const isFreightTypeFieldVisible =
    hasLoadedOrderDefinitions &&
    !hasFreightQuote &&
    orderDefinitions.orderTransportCondition !== OrderTransportCondition.NONE;

  const orderChargeTypeOptions: OrderConditionCodeOption[] = orderChargeTypesOptions.map(
    (option) => ({
      code: String(option.value),
      label: `${translate(option.label)} (${option.value})`,
    }),
  );

  const classificationTypeOptions: OrderConditionCodeOption[] =
    classificationTypesResource.leftItems.map((type) => ({
      code: type.code,
      label: `${type.code} - ${type.name}`,
    }));

  const classificationOptions: OrderConditionCodeOption[] = (
    selectedClassificationType?.orderClassifications ?? []
  ).map((classification) => ({
    code: classification.code,
    label: `${classification.code} - ${classification.name}`,
  }));

  const content = (
    <Stack sx={{ gap: 2, pt: 1 }}>
      {isBrand && (
        <Controller
          name="orderCollectionId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<Collection>
              source={{ url: 'collections', queryField: 'query' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_ORDER_COLLECTION)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              initialData={condition.collection ? [condition.collection] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'orderCollectionId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {isBrand && generalSettings.enableSaleType && (
        <Controller
          name="saleTypeId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<SaleType>
              source={{ url: '/sale-types', queryField: 'query' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_SALE_TYPE)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              initialData={condition.saleType ? [condition.saleType] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'saleTypeId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.showLocals && (
        <Controller
          name="branchId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<Local>
              source={{ url: '/locals', queryField: 'nameOrCode', order: { code: 'ASC' } }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_ORDER_BRANCH)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              initialData={condition.local ? [condition.local] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'branchId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {isShippingCompanyFieldVisible && (
        <Controller
          name="shippingCompanyId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<ShippingCompany>
              source={{ url: '/shipping-companies' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_SHIPPING_COMPANY)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) =>
                `${option.code} - ${option.commercialName || option.companyName} ${formatCNPJ(option.cnpj)}`
              }
              initialData={condition.shippingCompany ? [condition.shippingCompany] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'shippingCompanyId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {isFreightTypeFieldVisible && (
        <Controller
          name="brandFreightTypeId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<BrandFreightType>
              source={{ url: '/freight-type' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_FREIGHT_TYPE)}
              value={field.value ?? null}
              onChange={(value, options) => {
                field.onChange(value);
                setBrandFreightTypeCode(
                  options.find((option) => option?.id === value)?.code ?? null,
                );
              }}
              getOptionLabel={(option) => option.description}
              initialData={condition.brandFreightType ? [condition.brandFreightType] : []}
              error={!!fieldState.error || fieldsWithError.has('brandFreightTypeId')}
              helperText={fieldState.error?.message}
            />
          )}
        />
      )}

      {showClassification && (
        <>
          <Controller
            name="orderClassificationTypeCode"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete<OrderConditionCodeOption>
                size="small"
                disabled={isReadOnlyView}
                options={classificationTypeOptions}
                value={
                  classificationTypeOptions.find((option) => option.code === field.value) ?? null
                }
                onBlur={field.onBlur}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                onChange={(_, option) => {
                  field.onChange(option?.code ?? null);
                  setValue('orderClassificationCode', null);
                }}
                textFieldProps={{
                  label: translate(LangEnum.GLOBAL_CLASSIFICATION_TYPE),
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />

          <Controller
            name="orderClassificationCode"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete<OrderConditionCodeOption>
                size="small"
                disabled={isReadOnlyView || !selectedClassificationType}
                options={classificationOptions}
                value={classificationOptions.find((option) => option.code === field.value) ?? null}
                onBlur={field.onBlur}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                onChange={(_, option) => field.onChange(option?.code ?? null)}
                textFieldProps={{
                  label: translate(LangEnum.GLOBAL_CLASSIFICATION),
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </>
      )}

      {orderDefinitions.hasOrderOperationsIntegration && isBrand && (
        <Controller
          name="orderOperationCode"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<OrderOperationsIntegrationType>
              source={{ url: '/order-operations' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.ORDER_CONDITIONS_ORDER_OPERATION_TYPE_LABEL)}
              value={field.value ?? null}
              onChange={(value, options) => {
                field.onChange(value);
                setSelectedOrderOperation(options[0] ?? null);
              }}
              getOptionId={(option) => String(option.code)}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              error={hasFieldError(fieldState, fieldsWithError, 'orderOperationCode')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.hasOrderChargeTypeIntegration && isBrand && (
        <Controller
          name="orderChargeTypeCode"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete<OrderConditionCodeOption>
              size="small"
              disabled={isReadOnlyView}
              options={orderChargeTypeOptions}
              value={orderChargeTypeOptions.find((option) => option.code === field.value) ?? null}
              onBlur={field.onBlur}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              onChange={(_, option) => field.onChange(option?.code ?? null)}
              textFieldProps={{
                label: translate(LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_AUTO_COMPLETE_LABEL),
                error: hasFieldError(fieldState, fieldsWithError, 'orderChargeTypeCode'),
                helperText: fieldState.error?.message,
              }}
            />
          )}
        />
      )}

      {orderDefinitions.batchItemsIntegration && isBrand && (
        <Controller
          name="orderBatchItemCode"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<OrderBatchItemsType>
              source={{ url: '/order-batch-items' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.ORDER_CONDITIONS_ORDER_BATCH_ITEMS_LABEL)}
              value={field.value ?? null}
              onChange={(value, options) => {
                field.onChange(value);
                setSelectedOrderBatchItem(options[0] ?? null);
              }}
              getOptionId={(option) => option.code}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              error={hasFieldError(fieldState, fieldsWithError, 'orderBatchItemCode')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.showEmitInvoice && (
        <Controller
          name="emitInvoice"
          control={control}
          render={({ field }) => (
            <Switch
              label={translate(LangEnum.ORDER_CONDITIONS_EMIT_INVOICE)}
              checked={!!field.value}
              disabled={isReadOnlyView}
              onChange={(_, checked) => field.onChange(checked)}
            />
          )}
        />
      )}

      {customFields.map((config) => (
        <OrderConditionCustomFieldControl
          key={config.id}
          config={config}
          value={customFieldValues[config.id]}
          isDisabled={isReadOnlyView}
          onChange={(value) => setCustomFieldValue(config.id, value)}
        />
      ))}
    </Stack>
  );

  const footer: ModalFooterConfig = isReadOnlyView
    ? { primaryAction: { label: LangEnum.GLOBAL_CLOSE, onClick: onClose } }
    : {
        primaryAction: {
          label: LangEnum.GLOBAL_SAVE,
          isLoading: isSaving || isLoadingOrderDefinitions,
          isDisabled: !hasLoadedOrderDefinitions,
          onClick: save,
        },
        secondaryAction: {
          label: LangEnum.GLOBAL_CANCEL_LABEL,
          onClick: onClose,
        },
      };

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="expanded"
        onClose={onClose}
        footer={footer}
        header={{ title: LangEnum.ORDER_CONDITION_ADITIONAL_CONDITION_TITLE }}
      >
        {content}
      </Modal>

      <OrderConditionApplyToAllModal {...applyToAll} />
    </>
  );
};

export default OrderConditionDefinitionsModal;
